import AppError from './AppError';

type Result<TData, TError> = Ok<TData> | Err<TError>;
type Ok<TData> = {
  is_ok: true;
  data: TData;
};
type Err<TError> = {
  is_ok: false;
  error: TError;
};
// 1 -  create result
export function Ok<TData>(data: TData): Ok<TData> {
  return {
    is_ok: true,
    data,
  };
}

export function Err<TError>(error: TError): Err<TError> {
  return {
    is_ok: false,
    error,
  };
}
// utils to assert err

export function optionalCatch<TError = unknown, TData = unknown>(
  fn: () => TData,
): Result<TData, TError> {
  try {
    return Ok(fn());
  } catch (_error: unknown) {
    const error = _error as TError;
    return Err(error);
  }
}
// Hopeful for: https://github.com/microsoft/TypeScript/issues/26242
export async function asyncCatch<TError = unknown, TData = unknown>(
  pr: Promise<TData>,
): Promise<Result<TData, TError>> {
  try {
    const data = await pr;
    return Ok(data);
  } catch (_error: unknown) {
    const error = _error as TError;
    return Err(error);
  }
}
// Todo, remove result in favour of passThrough
// 2 - consume
export function unwrap<TData, TError>(result: Result<TData, TError>): TData {
  if (result.is_ok) {
    return result.data;
  }

  throw new AppError('Called Result.unwrap on an err value');
}

export function unwrapOr<TData, TError>(
  result: Result<TData, TError>,
  fallback: TData,
): TData {
  if (result.is_ok) {
    return result.data;
  }
  return fallback;
}
/**
 * Unwrap or expect Ok,
 */
export function expect<TData, TError>(
  result: Result<TData, TError>,
  msg: string,
): TData {
  if (result.is_ok) {
    return result.data;
  }

  throw new AppError(msg);
}

/**
 * Unwrap or assert the error type and rethrow as needed
 */
export function mapErr<TData, TError, TNew>(
  result: Result<TData, TError>,
  fn: (err: TError) => TNew,
): Result<TData, TNew> {
  if (result.is_ok) {
    return result;
  }
  return Err(fn(result.error));
}
